import {
  BorderBox,
  Box,
  FlexBox,
  PositionRelative,
  Text,
  TextInline,
} from '@trmediaab/zebra';
import { differenceInYears } from 'date-fns';

import { parseCarriage, parseShoes } from '~/utils/equipment';
import { EMPTY_VALUE_CHARACTER, formatOdds } from '~/utils/numbers';

import { Carriage } from '../Carriage';
import { Shoes } from '../Shoes';

export const TextCell = ({ children, sx, ...props }) => (
  <FlexBox alignItems="center">
    <Text
      flex="1"
      fontSize="1"
      color="brands.travrondenSpel"
      sx={{
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        ...sx,
      }}
      {...props}
    >
      {children}
    </Text>
  </FlexBox>
);

export const renderHorseNumber = ({ start, betType }) => (
  <FlexBox gridRow="span 2" alignItems="flex-start" px="3">
    <BorderBox
      display="flex"
      justifyContent="center"
      alignItems="center"
      borderRadius="4px"
      minWidth="24px"
      height="28px"
      backgroundColor={
        start.scratched
          ? 'text.tertiary'
          : betType != null
            ? `betType.${betType}.bg`
            : 'brands.travrondenSpel'
      }
    >
      <TextCell fontWeight="bold" color="white">
        {start.start_number}
      </TextCell>
    </BorderBox>
  </FlexBox>
);

export const renderHorseName = ({ start }) => (
  <TextCell
    fontWeight="bold"
    color={start.scratched ? 'text.tertiary' : 'brands.travrondenSpel'}
    textDecoration={start.scratched ? 'line-through' : undefined}
  >
    {start.horse.name}
    {start.horse.raised != null && start.horse.raised !== 'SE' && (
      <TextInline color="text.tertiary">
        &nbsp;({start.horse.raised})
      </TextInline>
    )}

    <TextInline textTransform="lowercase" color="text.tertiary">
      &nbsp;
      {start.horse.sex}
      {start.horse.birth != null &&
        differenceInYears(new Date(), new Date(start.horse.birth))}
    </TextInline>
  </TextCell>
);

export const renderDriver = ({ start, variant }) => (
  <TextCell fontWeight="medium">
    {start.driver_detail.name}

    {['tablet', 'tabletCompact', 'mobile'].includes(variant) && (
      <TextInline fontWeight="medium" color="text.tertiary">
        &nbsp; ({start.trainer_detail.short_name})
      </TextInline>
    )}
  </TextCell>
);

export const renderTrainer = ({ start }) => (
  <TextCell fontWeight="medium">{start.trainer_detail.name}</TextCell>
);

export const renderShoes = ({ start }) =>
  start.scratched ? (
    <TextCell fontWeight="bold" color="text.tertiary" textAlign="right">
      -
    </TextCell>
  ) : (
    <FlexBox alignItems="center" justifyContent="flex-end">
      <Shoes
        data={parseShoes(start)}
        fontSize="1"
        fontWeight="bold"
        textAlign="right"
        tooltipEnabled
      />
    </FlexBox>
  );

export const renderCarriage = ({ start }) =>
  start.scratched ? (
    <TextCell fontWeight="bold" color="text.tertiary" textAlign="right">
      -
    </TextCell>
  ) : (
    <FlexBox alignItems="center" justifyContent="flex-end">
      <Carriage
        data={parseCarriage(start)}
        fontSize="1"
        fontWeight="bold"
        tooltipEnabled
      />
    </FlexBox>
  );

export const renderGamePercentage = ({ start, betType }) => (
  <TextCell fontWeight="bold" textAlign="right">
    {start.game_percent[betType]
      ? `${formatOdds(start.game_percent[betType], 1, 1)}%`
      : '-'}
  </TextCell>
);

export const renderTrend = ({ start, betType }) => {
  const trend = start.game_percent.providers?.ATG?.[betType]?.trend;
  const formatted = `${trend > 0 ? '+' : ''}${formatOdds(trend, 1, 1)}`;

  return (
    <TextCell color="text.tertiary" fontWeight="bold" textAlign="right">
      {['-0,0', '+0,0', '0,0'].includes(formatted) ? '0,0' : formatted}
    </TextCell>
  );
};

export const renderOdds = ({ start }) => (
  <TextCell fontWeight="semiBold" textAlign="right">
    {formatOdds(start.odds.providers.ATG?.['V'])}
  </TextCell>
);

export const renderTrackInfo = ({ start }) => (
  <TextCell fontWeight="semiBold" textAlign="right" pr="3">
    {start.scratched
      ? EMPTY_VALUE_CHARACTER
      : `${start.distance}:${start.start_position}`}
  </TextCell>
);

export const renderHeaderTitle = ({ label, textAlign, ...props }) => (
  <PositionRelative display="flex" alignItems="center">
    <Text
      fontSize="0"
      fontWeight="bold"
      color="white"
      {...props}
      sx={{
        position: 'absolute',
        right: textAlign === 'right' ? '0' : undefined,
      }}
    >
      {label}
    </Text>
  </PositionRelative>
);

export const renderSpacer = () => <Box data-spacer />;
